import {
  Autocomplete,
  FilterOptionsState,
  InputAdornment,
  ListItem,
  TextField,
} from '@mui/material';

import React, { useCallback, useMemo, useState } from 'react';

import {
  PlainObject,
  UserForSearch,
  UserWithPermissions,
} from '../../../types';

import { useMapUsersForSearch } from '../../../components/usersSearchBox/UsersSearchBox.utils';
import { cn } from '../../../utils';
import { SearchListBoxComponent } from '../../../components/usersSearchBox/components/SearchListboxComponent';
import Fuse from 'fuse.js';
import { MemberInfoCell } from '../../../components/tableCellRenders';
import SearchIcon from '@mui/icons-material/SearchRounded';

interface Props {
  options: UserWithPermissions[];
  handleChange: (
    _: React.SyntheticEvent<Element, Event>,
    newValue: UserForSearch | string | null,
  ) => boolean | void;
}

export const EmailRecipientSearch = ({ options, handleChange }: Props) => {
  const [inputValue, setInputValue] = useState('');
  const searchOptions = useMapUsersForSearch(options);
  const fuse = useMemo(
    () =>
      new Fuse(searchOptions || [], {
        threshold: 0.35,
        keys: ['names', 'fullName'],
      }),
    [searchOptions],
  );

  const handleFilterOptions = useCallback(
    (
      searchOptions: (UserForSearch | string)[],
      state: FilterOptionsState<UserForSearch | string>,
    ) => {
      if (!state.inputValue) return searchOptions;
      return fuse.search(state.inputValue).map(({ item }) => item);
    },
    [fuse],
  );

  return (
    <Autocomplete<UserForSearch | string, false, false, true>
      freeSolo
      value={null}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'reset') {
          setInputValue('');
        } else {
          setInputValue(newInputValue);
        }
      }}
      loading={options === null}
      size="small"
      onChange={(event, newValue) => {
        const shouldClear = handleChange(event, newValue);
        if (shouldClear !== false) {
          setInputValue('');
        }
      }}
      options={searchOptions || []}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.fullName)}
      ListboxComponent={SearchListBoxComponent}
      className={cn('MuiAutocomplete-searchBox')}
      filterOptions={handleFilterOptions}
      renderOption={(props, opt) => {
        if (typeof opt === 'string') {
          return (
            <ListItem {...props} key={opt}>
              {opt}
            </ListItem>
          );
        }
        const {
          id,
          fullName,
          avatarUrl,
          designation,
          deactivatedAt,
          country,
          city,
        } = opt;
        return (
          <ListItem {...props} key={id}>
            <MemberInfoCell
              // eslint-disable-next-line
              hideAvatar={(props as PlainObject)['data-option-index'] > 2000}
              userInfo={{
                fullName: fullName,
                avatarUrl: avatarUrl,
                stream: designation,
                country: country,
                city: city,
              }}
              disabled={!!deactivatedAt}
            />
          </ListItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search or enter email group"
          InputProps={{
            ...(params.InputProps || {}),
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="disabled" />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};
